import { useEffect, useRef } from 'react';
import { Report, UserProfile } from '../types';
import { reconcileRepresentativeContacts } from '../services/projectRepresentatives';

interface Params {
    report: Report;
    availableUsers: UserProfile[];
    onUpdate: (report: Report) => void;
    isReadOnly?: boolean;
}

/**
 * Gleicht Telefon und E-Mail der Verantwortlichen beim Öffnen des Projekts
 * einmalig gegen die Profile ab.
 *
 * Hintergrund: Die Kontaktdaten werden beim Eintragen eines Vertreters EINMAL
 * aus dem Profil kopiert und danach nie wieder geprüft. Ein Slot, der früher
 * über das alte Stammdaten-Formular besetzt und später auf eine andere Person
 * umgeschrieben wurde, trägt bis heute die Nummer des Vorgängers — sie steht
 * dann in jedem neu erzeugten Begehungs-PDF unter dem falschen Namen.
 *
 * Die Regeln stecken vollständig in `reconcileRepresentativeContacts` (pur und
 * getestet); hier bleibt nur der Auslöser.
 *
 * Zwei Riegel gegen unerwünschte Schreibvorgänge:
 *   - `isReadOnly`: Ein Nur-Ansicht-Projekt wird beim Lesen nicht angefasst.
 *     Selbstheilende Migrationen auf fremden Projektzeilen sind genau der
 *     Fehler, den der Schreibschutz verhindern soll.
 *   - `doneRef`: höchstens ein Abgleich je geöffnetem Projekt. Die Funktion ist
 *     zwar idempotent (sie meldet danach `null`), aber der Versuch soll gar
 *     nicht erst bei jedem Render neu anlaufen.
 */
export const useRepresentativeContactSync = ({ report, availableUsers, onUpdate, isReadOnly = false }: Params) => {
    const doneRef = useRef<string | null>(null);

    useEffect(() => {
        if (isReadOnly) return;
        if (!availableUsers || availableUsers.length === 0) return;

        const projectId = report.metadata?.globalData?.projectId;
        if (!projectId || projectId === 'temp-id') return;
        if (doneRef.current === projectId) return;
        doneRef.current = projectId;

        const nextMeta = reconcileRepresentativeContacts(report.metadata, availableUsers);
        if (!nextMeta) return;

        console.info('[Vertreter] Kontaktdaten aus den Profilen nachgezogen.');
        onUpdate({ ...report, metadata: nextMeta });
    }, [report, availableUsers, isReadOnly, onUpdate]);
};
